import { createSlice } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

const initialState = {
  isLoggedIn: false,
  userInfo: null,
  profile: null,
  cartItems: [], // Array of cart items from backend API (singles + combos)
  profileLoading: true,
  cartLoading: false,
};

const hejalSlice = createSlice({
  name: "hejal",
  initialState,
  reducers: {
    // ==========================================
    // MAIN CART ACTIONS - INTEGRATED WITH BACKEND
    // ==========================================

    // SET ENTIRE CART FROM API RESPONSE
    setCart: (state, action) => {
      const cartData = action.payload;
      
      if (Array.isArray(cartData)) {
        state.cartItems = cartData;
      } else if (cartData && Array.isArray(cartData.items)) {
        // Handle if API returns { items: [...] }
        state.cartItems = cartData.items;
      } else {
        state.cartItems = [];
      }
      
      console.log("Redux: Cart set with", state.cartItems.length, "items");
    },

    // ADD SINGLE ITEM TO CART (after successful API call)
    addItemToCart: (state, action) => {
      const newItem = action.payload;
      
      if (!newItem || !newItem._id) {
        console.warn("Redux: Invalid item data for addItemToCart", newItem);
        return;
      }
      
      // Check if item already exists in cart (for single items)
      const existingItemIndex = state.cartItems.findIndex(
        item => item._id === newItem._id
      );
      
      if (existingItemIndex !== -1) {
        // Update existing item with new data from API
        state.cartItems[existingItemIndex] = newItem;
        console.log("Redux: Updated existing cart item", newItem._id);
      } else {
        // Add new item
        state.cartItems.push(newItem);
        console.log("Redux: Added new cart item", newItem._id);
      }
    },

    // ADD COMBO TO CART (after successful API call)
    addComboToCart: (state, action) => {
      const newCombo = action.payload;
      
      if (!newCombo || !newCombo._id) {
        console.warn("Redux: Invalid combo data for addComboToCart", newCombo);
        return;
      }
      
      // Check if combo already exists in cart
      const existingComboIndex = state.cartItems.findIndex(
        item => item._id === newCombo._id && item.itemType === 'combo'
      );
      
      if (existingComboIndex !== -1) {
        // Update existing combo
        state.cartItems[existingComboIndex] = newCombo;
        console.log("Redux: Updated existing combo", newCombo._id);
      } else {
        // Add new combo
        state.cartItems.push(newCombo);
        console.log("Redux: Added new combo", newCombo._id);
      }
    },

    // UPDATE SINGLE ITEM QUANTITY (from API response) - works for both single and combo
    updateItemQuantity: (state, action) => {
      const { cartItemId, newQuantity } = action.payload;
      
      if (!cartItemId || typeof newQuantity !== 'number') {
        console.warn("Redux: Invalid data for updateItemQuantity", action.payload);
        return;
      }
      
      const item = state.cartItems.find(item => item._id === cartItemId);
      if (item) {
        const oldQuantity = item.quantity;
        item.quantity = Math.max(1, newQuantity);
        
        // Update totals for display
        if (item.itemType === 'single' && item.totals) {
          const pricePerUnit = item.pricing?.currentPrice || 0;
          item.totals.itemTotal = parseFloat((pricePerUnit * item.quantity).toFixed(2));
        } else if (item.itemType === 'combo' && item.totals && item.comboDetails) {
          const comboPricePerUnit = item.comboDetails.comboPrice || 0;
          item.totals.itemTotal = parseFloat((comboPricePerUnit * item.quantity).toFixed(2));
        }
        
        console.log(`Redux: Updated quantity for ${cartItemId}: ${oldQuantity} → ${item.quantity}`);
      } else {
        console.warn("Redux: Item not found for quantity update", cartItemId);
      }
    },

    // REMOVE SINGLE ITEM FROM CART - works for both single and combo
    removeItem: (state, action) => {
      const { cartItemId } = action.payload;
      
      if (!cartItemId) {
        console.warn("Redux: Invalid cartItemId for removeItem", action.payload);
        return;
      }
      
      const initialLength = state.cartItems.length;
      const itemToRemove = state.cartItems.find(item => item._id === cartItemId);
      
      state.cartItems = state.cartItems.filter(item => item._id !== cartItemId);
      
      if (state.cartItems.length < initialLength) {
        const itemType = itemToRemove?.itemType || 'unknown';
        console.log(`Redux: Removed ${itemType} cart item`, cartItemId);
      } else {
        console.warn("Redux: Item not found for removal", cartItemId);
      }
    },

    // CLEAR ENTIRE CART
    clearCart: (state) => {
      const itemCount = state.cartItems.length;
      state.cartItems = [];
      console.log(`Redux: Cleared cart (${itemCount} items removed)`);
    },

    // SET CART LOADING STATE
    setCartLoading: (state, action) => {
      state.cartLoading = Boolean(action.payload);
    },

    // ==========================================
    // AUTH ACTIONS
    // ==========================================

    setAuthenticated: (state, action) => {
      state.isLoggedIn = Boolean(action.payload);
      
      if (!state.isLoggedIn) {
        // Clear cart when user logs out
        state.cartItems = [];
        state.profile = null;
        state.userInfo = null;
      }
    },

    setUserInfo: (state, action) => {
      state.userInfo = action.payload;
    },

    setProfile: (state, action) => {
      state.profile = action.payload;
    },

    setProfileLoading: (state, action) => {
      state.profileLoading = Boolean(action.payload);
    },

    setTokenInCookie: (state, action) => {
      const { token, refreshToken } = action.payload;
      if (token) {
        Cookies.set("accessToken", token, { expires: 7 });
        console.log("Redux: Access token set in cookie");
      }
      if (refreshToken) {
        Cookies.set("refreshToken", refreshToken, { expires: 30 });
        console.log("Redux: Refresh token set in cookie");
      }
    },

    logout: (state) => {
      // Clear all user data
      state.isLoggedIn = false;
      state.userInfo = null;
      state.profile = null;
      state.cartItems = [];
      state.profileLoading = false;
      state.cartLoading = false;
      
      // Remove cookies
      Cookies.remove("accessToken");
      Cookies.remove("refreshToken");
      
      console.log("Redux: User logged out, all data cleared");
    },

    // ==========================================
    // HELPER ACTIONS
    // ==========================================

    toggleAuthentication: (state) => {
      state.isLoggedIn = !state.isLoggedIn;
      
      if (!state.isLoggedIn) {
        state.cartItems = [];
        state.profile = null;
        state.userInfo = null;
      }
    },

    initializeSession: (state) => {
      const accessToken = Cookies.get("accessToken");
      const refreshToken = Cookies.get("refreshToken");
      
      if (accessToken && refreshToken) {
        state.isLoggedIn = true;
        console.log("Redux: Session initialized from cookies");
      } else {
        state.isLoggedIn = false;
        console.log("Redux: No valid session found");
      }
    },

    // ==========================================
    // BACKWARD COMPATIBILITY ACTIONS
    // ==========================================

    // DEPRECATED: Use addItemToCart instead
    addToCart: (state, action) => {
      console.warn("DEPRECATED: addToCart action is deprecated, use addItemToCart instead");
      
      const item = action.payload;
      if (item && item.productId) {
        const cartItem = {
          _id: item._id || `temp_${Date.now()}`,
          itemType: 'single',
          product: item.product || { _id: item.productId },
          quantity: item.quantity || 1
        };
        
        hejalSlice.caseReducers.addItemToCart(state, { payload: cartItem });
      }
    },

    deleteItem: (state, action) => {
      console.warn("DEPRECATED: deleteItem action is deprecated, use removeItem instead");
      
      const { cartId, _id } = action.payload;
      const itemId = cartId || _id;
      
      if (itemId) {
        hejalSlice.caseReducers.removeItem(state, { payload: { cartItemId: itemId } });
      }
    },

    increaseQuantity: (state, action) => {
      console.warn("DEPRECATED: increaseQuantity action is deprecated, use updateItemQuantity instead");
      
      const { _id, variantId } = action.payload;
      const itemId = _id || variantId;
      
      const item = state.cartItems.find(item => item._id === itemId);
      if (item) {
        hejalSlice.caseReducers.updateItemQuantity(state, {
          payload: { cartItemId: itemId, newQuantity: item.quantity + 1 }
        });
      }
    },

    decreaseQuantity: (state, action) => {
      console.warn("DEPRECATED: decreaseQuantity action is deprecated, use updateItemQuantity instead");
      
      const { _id, variantId } = action.payload;
      const itemId = _id || variantId;
      
      const item = state.cartItems.find(item => item._id === itemId);
      if (item) {
        hejalSlice.caseReducers.updateItemQuantity(state, {
          payload: { cartItemId: itemId, newQuantity: Math.max(1, item.quantity - 1) }
        });
      }
    },
  },
});

export const {
  // MAIN CART ACTIONS (USE THESE)
  setCart,
  addItemToCart,
  addComboToCart, // NEW
  updateItemQuantity,
  removeItem,
  clearCart,
  setCartLoading,
  
  // AUTH ACTIONS
  setAuthenticated,
  setUserInfo,
  setProfile,
  setProfileLoading,
  setTokenInCookie,
  logout,
  toggleAuthentication,
  initializeSession,
  
  // BACKWARD COMPATIBILITY (DEPRECATED)
  addToCart,
  deleteItem,
  increaseQuantity,
  decreaseQuantity,
} = hejalSlice.actions;

// ALIASES FOR BACKWARD COMPATIBILITY
export const resetCart = clearCart;
export const setCartItems = setCart;
export const initializeCart = setCart;

// UPDATED SELECTORS FOR SINGLES + COMBOS
export const selectCartItems = (state) => state.hejal.cartItems || [];

export const selectCartItemCount = (state) => {
  const items = state.hejal.cartItems || [];
  return items.reduce((count, item) => count + (item.quantity || 1), 0);
};

export const selectCartTotal = (state) => {
  const items = state.hejal.cartItems || [];
  return items.reduce((total, item) => {
    if (item.itemType === 'combo' && item.comboDetails) {
      // For combos, use comboPrice * quantity
      return total + (item.comboDetails.comboPrice * (item.quantity || 1));
    } else {
      // For single items, use current price or fallback
      const price = item.pricing?.currentPrice || item.totals?.itemTotal || 0;
      return total + (price * (item.quantity || 1));
    }
  }, 0);
};

// NEW SELECTORS FOR COMBO SPECIFIC DATA
export const selectSingleItems = (state) => {
  const items = state.hejal.cartItems || [];
  return items.filter(item => item.itemType === 'single');
};

export const selectComboItems = (state) => {
  const items = state.hejal.cartItems || [];
  return items.filter(item => item.itemType === 'combo');
};

export const selectCartSummary = (state) => {
  const items = state.hejal.cartItems || [];
  const singles = items.filter(item => item.itemType === 'single');
  const combos = items.filter(item => item.itemType === 'combo');
  
  const singleTotal = singles.reduce((total, item) => {
    const price = item.pricing?.currentPrice || 0;
    return total + (price * (item.quantity || 1));
  }, 0);
  
  const comboTotal = combos.reduce((total, item) => {
    const price = item.comboDetails?.comboPrice || 0;
    return total + (price * (item.quantity || 1));
  }, 0);
  
  return {
    totalItems: items.length,
    totalQuantity: items.reduce((count, item) => count + (item.quantity || 1), 0),
    singleItems: singles.length,
    comboItems: combos.length,
    singleTotal: parseFloat(singleTotal.toFixed(2)),
    comboTotal: parseFloat(comboTotal.toFixed(2)),
    grandTotal: parseFloat((singleTotal + comboTotal).toFixed(2))
  };
};

export default hejalSlice.reducer;